import { useEffect, useMemo, useState } from 'react';
import {
  AddCircleOutlineSharp,
  ConfirmationNumberOutlined,
  ContentCopyOutlined,
  ExpandMoreRounded,
  PersonOutline,
  SearchRounded,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { ccyFormat } from '../../helpers/helper';
import { STATUS_STORE_CREDIT } from '../../helpers/enums';
import StoreCreditService from '../../services/storeCredits';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: STATUS_STORE_CREDIT.PENDING, label: 'Pendiente' },
  { value: STATUS_STORE_CREDIT.USED, label: 'Usado' },
  { value: STATUS_STORE_CREDIT.EXPIRED, label: 'Vencido' },
  { value: STATUS_STORE_CREDIT.CANCELED, label: 'Cancelado' },
];

const STATUS_COLORS = {
  [STATUS_STORE_CREDIT.PENDING]: 'warning',
  [STATUS_STORE_CREDIT.USED]: 'success',
  [STATUS_STORE_CREDIT.EXPIRED]: 'default',
  [STATUS_STORE_CREDIT.CANCELED]: 'error',
};

const STATUS_ICON_BG = {
  [STATUS_STORE_CREDIT.PENDING]: '#fff3e0',
  [STATUS_STORE_CREDIT.USED]: '#e8f5e9',
  [STATUS_STORE_CREDIT.EXPIRED]: '#eeeeee',
  [STATUS_STORE_CREDIT.CANCELED]: '#ffebee',
};

const STATUS_ICON_COLOR = {
  [STATUS_STORE_CREDIT.PENDING]: '#e65100',
  [STATUS_STORE_CREDIT.USED]: '#2e7d32',
  [STATUS_STORE_CREDIT.EXPIRED]: '#616161',
  [STATUS_STORE_CREDIT.CANCELED]: '#c62828',
};

const extractErrorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || (typeof err?.response?.data === 'string' ? err.response.data : null) || fallback;

const PAGE_SIZE = 5;

const formatDate = (isoDate) => {
  if (!isoDate) return null;
  try {
    return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(isoDate));
  } catch {
    return null;
  }
};

const StoreCreditsPanel = ({ storeId }) => {
  const [statusFilter, setStatusFilter] = useState(STATUS_STORE_CREDIT.PENDING);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [searchCode, setSearchCode] = useState('');
  const [searchName, setSearchName] = useState('');
  const [applyState, setApplyState] = useState({});
  const [cancellingId, setCancellingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ amount: '', reason: '', name_sale: '' });
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadCredits = async (status, nameSale, targetPage = 1) => {
    if (!storeId) return;
    setLoading(true);
    try {
      const resp = await StoreCreditService.getAll(storeId, status || undefined, nameSale || undefined, targetPage, PAGE_SIZE);
      if (Array.isArray(resp?.items)) {
        setCredits(resp.items);
        setPage(resp.page || targetPage);
        setTotalPages(resp.total_pages || 1);
        setTotalCount(resp.total ?? resp.items.length);
      } else {
        setCredits(Array.isArray(resp) ? resp : []);
        setPage(1);
        setTotalPages(1);
        setTotalCount(Array.isArray(resp) ? resp.length : 0);
      }
    } catch (err) {
      setFeedback({ severity: 'error', message: extractErrorMessage(err, 'No se pudieron cargar los vales') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCredits(statusFilter, searchName.trim(), 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, statusFilter]);

  const handleChangePage = (_e, newPage) => {
    loadCredits(statusFilter, searchName.trim(), newPage);
  };

  const handleSearchName = () => {
    setSearchCode('');
    loadCredits(statusFilter, searchName.trim(), 1);
  };

  const handleSearchCode = async () => {
    const code = searchCode.trim();
    if (!code) {
      loadCredits(statusFilter, searchName.trim(), 1);
      return;
    }
    setSearchName('');
    setLoading(true);
    try {
      const resp = await StoreCreditService.getByCode(storeId, code);
      setCredits(resp ? [resp] : []);
      setPage(1);
      setTotalPages(1);
      setTotalCount(resp ? 1 : 0);
    } catch (err) {
      setFeedback({ severity: 'error', message: extractErrorMessage(err, 'Vale no encontrado') });
      setCredits([]);
      setPage(1);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (credit) => {
    setCancellingId(credit._id);
    try {
      await StoreCreditService.cancel(storeId, credit._id);
      setFeedback({ severity: 'success', message: `Vale ${credit.code} cancelado` });
      await loadCredits(statusFilter, searchName.trim(), page);
    } catch (err) {
      setFeedback({ severity: 'error', message: extractErrorMessage(err, 'No se pudo cancelar el vale') });
    } finally {
      setCancellingId(null);
    }
  };

  const handleApply = async (credit) => {
    const state = applyState[credit.code] || {};
    const saleId = (state.saleId || '').trim();
    if (!saleId) {
      setFeedback({ severity: 'error', message: 'Ingresá el id de la venta donde se canjea el vale' });
      return;
    }
    const balance = Number(credit.balance ?? credit.amount ?? 0);
    const amount = state.amount === undefined || state.amount === '' ? undefined : Number(state.amount);
    if (amount !== undefined && (amount <= 0 || amount > balance)) {
      setFeedback({ severity: 'error', message: `El monto a canjear debe ser mayor a 0 y no superar el saldo ($${ccyFormat(balance)})` });
      return;
    }
    setApplyState((prev) => ({ ...prev, [credit.code]: { ...state, applying: true } }));
    try {
      await StoreCreditService.apply(storeId, credit.code, saleId, amount);
      setFeedback({ severity: 'success', message: `Vale ${credit.code} canjeado correctamente` });
      setApplyState((prev) => ({ ...prev, [credit.code]: { saleId: '', amount: '', applying: false } }));
      await loadCredits(statusFilter, searchName.trim(), page);
    } catch (err) {
      setFeedback({ severity: 'error', message: extractErrorMessage(err, 'No se pudo canjear el vale') });
      setApplyState((prev) => ({ ...prev, [credit.code]: { ...state, applying: false } }));
    }
  };

  const handleCreate = async () => {
    const amount = Number(createForm.amount);
    if (!amount || amount <= 0) {
      setFeedback({ severity: 'error', message: 'El monto debe ser mayor a 0' });
      return;
    }
    setCreating(true);
    try {
      const created = await StoreCreditService.create(storeId, {
        amount,
        reason: createForm.reason || undefined,
        name_sale: createForm.name_sale || undefined,
      });
      setFeedback({ severity: 'success', message: `Vale ${created?.code} creado` });
      setCreateForm({ amount: '', reason: '', name_sale: '' });
      setShowCreateForm(false);
      setStatusFilter(STATUS_STORE_CREDIT.PENDING);
      await loadCredits(STATUS_STORE_CREDIT.PENDING, searchName.trim(), 1);
    } catch (err) {
      setFeedback({ severity: 'error', message: extractErrorMessage(err, 'No se pudo crear el vale') });
    } finally {
      setCreating(false);
    }
  };

  const handleCopyCode = async (e, code) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      setFeedback({ severity: 'success', message: `Código ${code} copiado` });
    } catch {
      // ignore clipboard errors
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const pendingTotal = useMemo(() => (
    credits
      .filter((c) => c.status === STATUS_STORE_CREDIT.PENDING)
      .reduce((sum, c) => sum + Number(c.balance ?? c.amount ?? 0), 0)
  ), [credits]);
  return (
    <Box display='flex' flexDirection='column' gap={1.5} p={2} sx={{ bgcolor: '#faf8f5' }}>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1}
        sx={{ borderRadius: 2, p: 1.5, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}
      >
        <Box display='flex' alignItems='center' gap={1}>
          <ConfirmationNumberOutlined color='secondary' />
          <Box>
            <Typography variant='subtitle1' fontWeight={700} lineHeight={1.1}>Vales</Typography>
            <Typography variant='caption' color='text.secondary'>
              {totalCount} vale{totalCount !== 1 ? 's' : ''}
              {totalPages <= 1 && pendingTotal > 0 ? ` · $${ccyFormat(pendingTotal)} pendientes` : ''}
            </Typography>
          </Box>
        </Box>
        <Button
          size='small'
          variant='contained'
          startIcon={<AddCircleOutlineSharp />}
          onClick={() => setShowCreateForm((prev) => !prev)}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Nuevo vale
        </Button>
      </Box>

      <Box
        display='flex'
        gap={1}
        flexWrap='wrap'
        alignItems='center'
        sx={{ borderRadius: 2, p: 1.25, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}
      >
        <Select
          size='small'
          value={statusFilter}
          onChange={(e) => { setSearchCode(''); setStatusFilter(e.target.value) }}
          sx={{ minWidth: 150, borderRadius: 2 }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        <TextField
          size='small'
          placeholder='Código'
          value={searchCode}
          onChange={(e) => setSearchCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchCode() }}
          InputProps={{
            startAdornment: <InputAdornment position='start'><SearchRounded fontSize='small' color='disabled' /></InputAdornment>,
            sx: { borderRadius: 2 },
          }}
          sx={{ width: 170 }}
        />
        <TextField
          size='small'
          placeholder='Nombre'
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchName() }}
          InputProps={{
            startAdornment: <InputAdornment position='start'><PersonOutline fontSize='small' color='disabled' /></InputAdornment>,
            sx: { borderRadius: 2 },
          }}
          sx={{ width: 170 }}
        />
        <Button size='small' variant='outlined' sx={{ borderRadius: 2, textTransform: 'none' }} onClick={() => { handleSearchCode(); handleSearchName() }}>
          Buscar
        </Button>
      </Box>

      {showCreateForm && (
        <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1, border: '1px dashed', borderColor: 'secondary.main', bgcolor: '#fdf6ec' }}>
          <Typography variant='body2' fontWeight={600}>Crear vale manual</Typography>
          <Box display='flex' gap={1} flexWrap='wrap'>
            <TextField
              size='small'
              label='Monto'
              type='number'
              value={createForm.amount}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, amount: e.target.value }))}
              sx={{ width: 140 }}
            />
            <TextField
              size='small'
              label='Cliente (opcional)'
              value={createForm.name_sale}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, name_sale: e.target.value }))}
            />
            <TextField
              size='small'
              label='Motivo (opcional)'
              value={createForm.reason}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, reason: e.target.value }))}
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
          <Box display='flex' justifyContent='flex-end' gap={1}>
            <Button size='small' onClick={() => setShowCreateForm(false)}>Cancelar</Button>
            <Button size='small' variant='contained' disabled={creating} onClick={handleCreate}>
              {creating ? 'Creando...' : 'Crear'}
            </Button>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box display='flex' justifyContent='center' py={3}><CircularProgress size={28} /></Box>
      ) : (
        <Box display='flex' flexDirection='column' gap={1}>
          {credits.length === 0 && (
            <Typography variant='body2' color='text.secondary' textAlign='center' py={3}>
              No hay vales para mostrar
            </Typography>
          )}
          {credits.map((credit) => {
            const isPending = credit.status === STATUS_STORE_CREDIT.PENDING;
            const isExpanded = expandedId === credit._id;
            const state = applyState[credit.code] || {};
            const balance = credit.balance ?? credit.amount;

            return (
              <Paper
                key={credit._id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: 1 },
                }}
              >
                <Box
                  display='flex'
                  alignItems='center'
                  gap={1.5}
                  px={1.5}
                  py={1}
                  onClick={() => toggleExpanded(credit._id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Avatar sx={{ bgcolor: STATUS_ICON_BG[credit.status] || '#eee', width: 36, height: 36 }}>
                    <ConfirmationNumberOutlined sx={{ fontSize: 18, color: STATUS_ICON_COLOR[credit.status] || '#616161' }} />
                  </Avatar>

                  <Box flex={1} minWidth={0}>
                    {(credit.name_sale || credit.reason) && (
                      <Typography variant='overline' color='text.secondary' noWrap sx={{ display: 'block' }} fontWeight={700}>
                        {[credit.name_sale, credit.reason].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    <Box display='flex' alignItems='center' gap={0.75} flexWrap='wrap'>
                      <Typography variant='caption'>{credit.code}</Typography>
                      <ContentCopyOutlined
                        sx={{ fontSize: 15, cursor: 'pointer', color: 'text.secondary' }}
                        onClick={(e) => handleCopyCode(e, credit.code)}
                      />
                      <Chip size='small' label={credit.status} color={STATUS_COLORS[credit.status] || 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  </Box>

                  <Box textAlign='right' sx={{ flexShrink: 0 }}>
                    <Typography variant='subtitle2' fontWeight={700} color='primary.main'>
                      ${ccyFormat(balance)}
                    </Typography>
                    {balance !== credit.amount && (
                      <Typography variant='caption' color='text.secondary'>
                        de ${ccyFormat(credit.amount)}
                      </Typography>
                    )}
                  </Box>

                  <ExpandMoreRounded
                    sx={{
                      color: 'text.secondary',
                      transition: 'transform 0.2s ease',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </Box>

                <Collapse in={isExpanded} unmountOnExit>
                  <Divider />
                  <Box px={1.5} py={1.25} display='flex' flexDirection='column' gap={1} sx={{ bgcolor: '#fafafa' }}>
                    <Box display='flex' gap={2} flexWrap='wrap'>
                      <Typography variant='caption' color='text.secondary'>
                        Creado: {formatDate(credit.created_at) || '-'}
                      </Typography>
                      {credit.expires_at && (
                        <Typography variant='caption' color='text.secondary'>
                          Vence: {formatDate(credit.expires_at)}
                        </Typography>
                      )}
                      {credit.used_at && (
                        <Typography variant='caption' color='text.secondary'>
                          Último uso: {formatDate(credit.used_at)}
                        </Typography>
                      )}
                    </Box>

                    {Array.isArray(credit.usages) && credit.usages.length > 0 && (
                      <Box>
                        <Typography variant='caption' fontWeight={600} color='text.secondary'>Historial de canjes</Typography>
                        {credit.usages.map((usage, idx) => (
                          <Typography key={idx} variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                            ${ccyFormat(usage.amount)} · {formatDate(usage.used_at) || '-'}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {isPending && (
                      <Box display='flex' gap={1} alignItems='center' flexWrap='wrap' pt={0.5}>
                        <TextField
                          size='small'
                          label='ID de venta a canjear'
                          value={state.saleId || ''}
                          onChange={(e) => setApplyState((prev) => ({ ...prev, [credit.code]: { ...state, saleId: e.target.value } }))}
                          sx={{ minWidth: 220 }}
                        />
                        <TextField
                          size='small'
                          type='number'
                          label='Monto (vacío = todo el saldo)'
                          value={state.amount ?? ''}
                          onChange={(e) => setApplyState((prev) => ({ ...prev, [credit.code]: { ...state, amount: e.target.value } }))}
                          inputProps={{ min: 0, max: balance }}
                          sx={{ width: 200 }}
                        />
                        <Button
                          size='small'
                          variant='contained'
                          disabled={state.applying}
                          onClick={() => handleApply(credit)}
                          sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                          {state.applying ? 'Canjeando...' : 'Canjear'}
                        </Button>
                        <Box flex={1} />
                        <Button
                          size='small'
                          color='error'
                          disabled={cancellingId === credit._id}
                          onClick={() => handleCancel(credit)}
                          sx={{ textTransform: 'none' }}
                        >
                          {cancellingId === credit._id ? 'Cancelando...' : 'Cancelar vale'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {totalPages > 1 && (
        <Box display='flex' justifyContent='center' pt={0.5}>
          <Pagination
            size='small'
            page={page}
            count={totalPages}
            onChange={handleChangePage}
            disabled={loading}
            shape='rounded'
            color='secondary'
          />
        </Box>
      )}

      <Snackbar
        open={!!feedback}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'success'} onClose={() => setFeedback(null)} variant='filled'>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoreCreditsPanel;

